import Model, { attr, belongsTo, hasMany, type HasMany } from '@ember-data/model';
import type { Type } from '@warp-drive/core-types/symbols';
import { createLocalizedProxy, type CustomTranslations } from 'invite-sign/utils/translations';

import type Questionnaire from './questionnaire';
import type QuestionnaireField from './questionnaire-field';

// Map from camel-cases attribute name (used in templates) to dasherized version (present in JSON).
// If only Typescript had a `Dasherize` string manipulation type like it has `Uppercase` and `Lowercase`...
const TRANSLATABLE_ATTRIBUTE_MAPPING = {
  description: 'description',
  pdf: 'pdf',
  title: 'title',
  videoInstructions: 'video-instructions',
  videoUrl: 'video-url',
} as const;
type TranslatableAttribute = keyof typeof TRANSLATABLE_ATTRIBUTE_MAPPING;
type RawTranslatableAttribute = (typeof TRANSLATABLE_ATTRIBUTE_MAPPING)[TranslatableAttribute];

export interface PdfData {
  filename: string;
  'pdf-url': string;
  'updated-at': string; // represents a date but no transformation is applied to make this be a Date object
}

interface EmptyPdfData {
  filename: null;
  'pdf-url': null;
  'updated-at': null;
}

export default class QuestionnaireSection extends Model {
  declare [Type]: 'questionnaire-section]';

  @belongsTo('questionnaire', { async: false, inverse: 'questionnaireSections' })
  declare questionnaire: Questionnaire;

  @hasMany('questionnaire-field', { async: false, inverse: 'questionnaireSection' })
  declare questionnaireFields: HasMany<QuestionnaireField>;

  @attr('boolean') declare allowSkipVideo: boolean;
  @attr() declare customTranslations: CustomTranslations<RawTranslatableAttribute>;
  @attr('string') declare description: string;
  @attr() declare pdf?: PdfData | EmptyPdfData;
  @attr('number') declare position: number;
  @attr('string') declare title: string;
  @attr('string') declare videoInstructions: string;
  @attr('string') declare videoUrl: string;

  forLocale(locale: string, defaultLocale: string): this {
    if (locale !== defaultLocale) {
      return createLocalizedProxy(this, TRANSLATABLE_ATTRIBUTE_MAPPING, locale);
    }
    return this;
  }
}
